import request from 'sb-request'
import actions from 'redux/actions'
import { reducers, getState } from 'redux/core'
import { constants, object, links } from 'common/helpers'
import { getFeatureToggle, getCookieValue } from 'helpers'

import discountCodeQuery from './discountCodeQuery.graphql'


const setupInquire = (options) =>
  /**
   *
   * @param {object} values
   * @param {string} values.street
   * @param {string} values.zipCode
   * @param {string} values.state
   * @param {string} values.city
   * @param {number} [values.monthCount]
   */
  (values) => {
    const {
      endpoint, modifyResult,
      multiple = true,
      withErrorModal = true, withHumbleErrorModal = false,
    } = options

    const offer = getCookieValue(constants.cookieNames.offer)

    let inquireRequest

    if (inquireRequest) {
      inquireRequest.abort()
    }
    else {
      reducers.view.paymentPage.setFetching(true)
    }

    inquireRequest = request.get(endpoint, {
      query: {
        ...values,
        offer,
      },
      multiple,
      withErrorModal,
      withHumbleErrorModal,
      modifyResult: (data) => {
        const modifiedData = modifyResult(data)

        return {
          isFetching: false,
          ...modifiedData,
        }
      },
      onComplete: () => {
        inquireRequest = null
      },
    })

    return inquireRequest
      .then((result) => {
        reducers.view.paymentPage.set(result)
        return result
      }, () => {
        reducers.view.paymentPage.setFetching(false)
      })
  }


/**
 *
 * @param {Object} values
 * @param {string} values.street
 * @param {number} values.zipCode
 * @param {string} values.state
 * @param {string} values.city
 * @param {Object} options
 * @param {Boolean} options.withErrorModal
 * @param {Boolean} options.withHumbleErrorModal
 */
const inquireSubscription = (values, options = {}) => {
  const {
    view: {
      paymentPage: {
        selectedPlan = 'MONTHLY',
        coupon: { id, code, referralCoupon, isDisabled: isCouponDisabled },
      },
      pickPage: { selectedItems = [] },
    },
    models: { payment: { subscriptionCase } },
    routing: {
      location: { query: { planName: queryPlanName = '', resubscribeCode = '' } },
    },
  } = getState()

  const resubscribeCodeNormalized                 = resubscribeCode && String(resubscribeCode)
  const couponCode                                = resubscribeCodeNormalized || getCookieValue(constants.cookieNames.discountCoupon)
  const isPremiumProductsForNotSubscribedEnabled  = getFeatureToggle(constants.features.premiumProductsForNotSubscribed)

  if (isPremiumProductsForNotSubscribedEnabled && selectedItems.length) {
    values.pickedProducts = selectedItems.map(({ id }) => id).join()
  }

  if (couponCode) {
    if (!isCouponDisabled) {
      values.couponCode = couponCode
      values.couponType = 'order'
    }
  }
  else {
    values.coupon = isCouponDisabled ? null : id
  }

  const plan = queryPlanName.toUpperCase() || values.plan || selectedPlan

  const getCouponInfo = (newCouponCode) => {
    let couponInfo = code

    if (isCouponDisabled) {
      return couponInfo
    }

    if (newCouponCode) {
      couponInfo = newCouponCode
    }

    return couponInfo
  }

  const inquire = setupInquire({
    endpoint: 'rest/subscription/inquire',
    modifyResult: (response) => {
      const {
        subscription, cart, discount, tax, price, upchargePrice,
        priceWithCredits, coupon: newCouponCode, discountCode,
        credits, offer, ecommerceCredits, pickedProducts, subscriptionCasePrice,
        payPalAuthorizationFee, creditCardAuthorizationFee,
      } = response

      return {
        isSubscriptionExist: Boolean(subscription),
        isEcommerceExist: Boolean(cart),
        subscription: subscription ? object.removeKeys(subscription, [ 'tax' ]) : {},
        isEnoughVindiciaCreditsToBuy: priceWithCredits === 0,
        cart: cart || {},
        bill: {
          tax,
          price,
          offer,
          discount,
          upchargePrice,
          ecommerceCredits,
          payPalAuthorizationFee,
          credits: Math.abs(credits),
          creditCardAuthorizationFee: creditCardAuthorizationFee || 0,
        },
        coupon: {
          isFetching: false,
          code: getCouponInfo(newCouponCode),
          errors: discountCode && discountCode.errors ? discountCode.errors : null,
        },
        pickedProducts,
        subscriptionCasePrice,
      }
    },
    ...options,
  })

  return inquire({
    ...values,
    plan, // TODO move `plan` outside. And why this is `plan`? It must be `planName: "MONTHLY"`
    referralCoupon,
    subscriptionCase,
    clearCouponSession: isCouponDisabled,
  })
}

/**
 *
 * @param values
 * @returns {*}
 */
const inquireEcommerce = (values) => {
  const inquire = setupInquire({
    endpoint: 'rest/order/inquire',
    modifyResult: (response) => {
      const {
        credits, discount, price, priceWithCredits, shipping, tax, offer, appliedPriceRules,
        ecommerceCredits, payPalAuthorizationFee = null,
      } = response

      return {
        isSubscriptionExist: false,
        isEcommerceExist: true,
        // user's ecommerce credit applied to price always
        isEnoughEcommerceCreditsToBuy: price === 0,
        isEnoughVindiciaCreditsToBuy: priceWithCredits === 0,
        cart: {
          price: price - tax - shipping,
          shipping,
          discount,
        },
        bill: {
          discount,
          shipping,
          tax,
          price,
          // backend returns negative amount that will be charged from user's credit balance if user will use credits
          credits: Math.abs(credits),
          offer,
          ecommerceCredits,
          payPalAuthorizationFee,
        },
        appliedPriceRules,
      }
    },
  })

  return inquire(values)
}

/**
 *
 * @param {object} values
 * @param {string} values.street
 * @param {number} values.zipCode
 * @param {string} values.state
 * @param {string} values.city
 * @param {string} values.amount
 */
const inquireGiftCard = (values) => {
  const inquire = setupInquire({
    endpoint: 'rest/gift/card/inquire',
    modifyResult: ({ shipping, tax, discount, price, offer }) => ({
      isSubscriptionExist: false,
      isEcommerceExist: false,
      subscription: {},
      cart: {},
      bill: {
        shipping,
        tax,
        discount,
        price,
        offer,
      },
      coupon: {},
    }),
  })

  return inquire(values)
}

/**
 *
 * @param {object} values
 * @param {string} values.street
 * @param {number} values.zipCode
 * @param {string} values.state
 * @param {string} values.city
 * @param {string} values.monthCount
 */
const inquireGiftSubscription = (values) => {
  const { monthCount, ...restValues } = values
  const { view: { paymentPage: { coupon: { id, code, referralCoupon, isDisabled: isCouponDisabled } } } } = getState()

  const coupon = isCouponDisabled ? null : id

  const inquire = setupInquire({
    endpoint: 'rest/gift/subscription/inquire',
    modifyResult: ({ subscription, shipping, tax, discount, price, offer, coupon: newCouponCode }) => ({
      isSubscriptionExist: false,
      isEcommerceExist: false,
      subscription: subscription || {},
      cart: {},
      bill: {
        shipping,
        tax,
        discount,
        price,
        offer,
      },
      coupon: {
        isFetching: false,
        code: isCouponDisabled ? code : newCouponCode,
      },
    }),
  })

  return inquire({
    plan: monthCount,
    coupon,
    referralCoupon,
    ...restValues,
  })
}

/**
 *
 * @param {object} values
 * @param {string} values.coupon
 */
const inquireRedeemCoupon = (values) => {
  const { models: { me: { redeemCoupon: { coupon } } } } = getState()

  const inquire = setupInquire({
    endpoint: 'rest/coupon/redeem/inquire',
    modifyResult: ({
      shipping,
      tax,
      discount,
      price,
      credits,
      offer,
      payPalAuthorizationFee = null,
      creditCardAuthorizationFee = null,
      nextMonthPrice = null,
      ecommerceCredits,
    }) => ({
      isSubscriptionExist: false,
      isEcommerceExist: false,
      subscription: {},
      cart: {},
      bill: {
        shipping,
        tax,
        discount,
        price,
        credits,
        offer,
        payPalAuthorizationFee,
        creditCardAuthorizationFee,
        nextMonthPrice,
        ecommerceCredits,
      },
      coupon: {},
    }),
  })

  return inquire({ ...values, coupon })
}

/**
 * Loads information about discount code
 * @param {object} values
 * @param {string} values.code discount code
 * @param {string} values.plan override selected plan for the request
 * @param {string} values.type override type of the discount code
 * @param {boolean} [values.fromUser]
 * @param {boolean} [values.withHumbleErrorModal]
 */
const fetchDiscount = ({ code, plan, type, fromUser = false, withHumbleErrorModal }) => {
  const {
    view: {
      paymentPage: {
        selectedPlan,
        coupon: { withForcedLowerValue },
      },
    },
  } = getState()

  return request.post('graphql?name=discountCode', {
    withErrorModal: false,
    withHumbleErrorModal,
    withNestedErrors: true,
    body: {
      query: discountCodeQuery,
      variables: {
        code,
        type: type || 'order',
        plan: plan || selectedPlan,
        forceLowerValue: withForcedLowerValue,
        fromUser,
      },
    },
  })
    .then(
      (data) => {
        const { discountCode: { code: codeData, code: { couponType, marketingCouponOfferType } } } = data

        reducers.view.paymentPage.setCouponWithForcedLowerValue(false)

        // We have two coupon types marketing and discount
        if (couponType === 'marketing') {
          actions.misc.cookies.setItem(constants.cookieNames.giftCoupon, { code, offerType: marketingCouponOfferType })
          actions.models.user.bindCouponToUser()
            .then(() => actions.models.router.replace(links.couponPayment))
        }
        else {
          reducers.view.paymentPage.setCoupon({ ...codeData, error: null })

          return codeData
        }
      },
      ({ errors }) => {
        const error = errors && errors[0]

        if (error) {
          return Promise.reject({
            ...error,
            code,
            type: 'discountCode',
          })
        }
      }
    )
}

const syncResubscribeDiscount = () => {
  const { routing: { location: { query: { resubscribeCode } } } } = getState()

  const codeNormalized = resubscribeCode && String(resubscribeCode).toLowerCase()

  return codeNormalized ? fetchDiscount({ code: codeNormalized }) : Promise.resolve()
}

const toggleUseCredits = (value) => reducers.view.paymentPage.updateUseCredits(value)

const setSelectedPlan = (value) => reducers.view.paymentPage.setSelectedPlan(value)

const setBillingError = (value) => reducers.view.paymentPage.setBillingError(value)

const setCouponDisabled = (isDisabled) => {
  const { view: { paymentPage: { coupon: { code } } } } = getState()

  reducers.view.paymentPage.setCouponDisabled(isDisabled)

  if (!isDisabled && code) {
    fetchDiscount({ code })
  }
}

const setOrderInfoForApplePay = (values) => reducers.view.paymentPage.setOrderInfoForApplePay(values)

const setCouponWithForcedLowerValue = (value) => reducers.view.paymentPage.setCouponWithForcedLowerValue(value)

/**
 * Change Event For Clean CreditCardForm
 * FLOW :
 *  if user try buy subscription using prepaid card, he can show PrepaidCardWithDiscountModal
 *  when the modal window closes, need to scroll to CreditCardForm and clean the form fields
 * @param value {boolean}
 * @returns {*}
 */
const changeEventForCleanCreditCardForm = (value) => reducers.view.paymentPage.updateEventForCleanCreditCardForm(value)

/**
 * A helper to update payment method after adding subscription to user's cart on ecommerce page
 * @param value
 * @return {*}
 */
const setIsSubscriptionExist = (value) => reducers.view.paymentPage.set({ isSubscriptionExist: value })

const setFetching = reducers.view.paymentPage.setFetching


export default {
  inquireSubscription,
  inquireEcommerce,
  inquireGiftCard,
  inquireGiftSubscription,
  inquireRedeemCoupon,
  fetchDiscount,
  syncResubscribeDiscount,
  toggleUseCredits,
  setSelectedPlan,
  setBillingError,
  setCouponDisabled,
  setOrderInfoForApplePay,
  setCouponWithForcedLowerValue,
  changeEventForCleanCreditCardForm,
  setIsSubscriptionExist,
  setFetching,
}
